import { BossPhase1 } from './enemies/bossPhase1.js';
import { BossPhase2 } from './enemies/bossPhase2.js';
import { Drone } from './enemies/drone.js';
import { Enforcer } from './enemies/enforcer.js';
import { Minmon } from './enemies/minmon.js';
import { Zealot } from './enemies/zealot.js';
import { Zilua } from './enemies/zilua.js';

export function spawnEnemiesForRoom(game, roomNum, variantId = null, bossRooms = {}) {
  const phase = bossRooms[roomNum];
  if (phase) {
    const bossRadius = game._radiusForEnemyType('boss');
    const minPlayerDistance = game.runMode === 'run' ? 6 : 0;
    const defaultSpawn = game.room.enemySpawns[0] || game.room.playerSpawn || { x: 0, z: -4 };

    // Prefer validated encounter anchors for boss rooms; marker-based boss spawns
    // can occasionally end up outside the playable area in authored maps.
    const anchorSpawn = (game.room.enemySpawns || []).find((anchor) => game._isSpawnPointClear(anchor.x, anchor.z, bossRadius, {
      minPlayerDistance,
    })) || null;
    const requestedSpawn = anchorSpawn || game.room.bossSpawn || defaultSpawn;

    let spawn = requestedSpawn;
    const clearAtRequested = game._isSpawnPointClear(spawn.x, spawn.z, bossRadius, {
      minPlayerDistance,
    });
    if (!clearAtRequested) {
      const safe = game._findSafeSummonSpawn('boss', spawn.x, spawn.z, {
        attempts: 40,
        minRadius: 1.2,
        maxRadius: 11,
        minPlayerDistance,
      });
      spawn = safe || defaultSpawn;
    }
    const BossCtor = phase === 2 ? BossPhase2 : BossPhase1;
    const boss = new BossCtor({
      scene: game.scene,
      x: spawn.x,
      z: spawn.z,
      audio: game.audio,
      fbx: phase === 2 ? game._boss02Fbx : game._boss01Fbx,
    });
    game.enemies.push(boss);
    game.audio.playMusic('boss');
    const visualLabel = boss.usingFbxVisual ? 'FBX' : 'PROC';
    const label = phase === 1 ? `MINI-BOSS (${visualLabel})` : `FINAL BOSS (${visualLabel})`;
    game.toast(label);
    return;
  }

  game.audio.playMusic('combat');

  // Build spawn composition based on roomNum (1-10 excluding 5,10)
  const comp = game._composeSpawn(roomNum, variantId);
  const minSpawnDistance = 10;
  const maxSpawnDistance = 20;
  const playerPos = game.player || game.room.playerSpawn || { x: 0, z: 0 };
  let anchors = game.room.enemySpawns.slice();
  // Shuffle anchors
  for (let i = anchors.length - 1; i > 0; i--) {
    const j = Math.floor(game.rng() * (i + 1));
    [anchors[i], anchors[j]] = [anchors[j], anchors[i]];
  }

  const preferredAnchors = anchors.filter((a) => {
    const dx = a.x - playerPos.x;
    const dz = a.z - playerPos.z;
    const distanceSq = dx * dx + dz * dz;
    return distanceSq >= (minSpawnDistance * minSpawnDistance) && distanceSq <= (maxSpawnDistance * maxSpawnDistance);
  });
  if (preferredAnchors.length > 0) {
    const remainingAnchors = anchors.filter((a) => !preferredAnchors.includes(a));
    anchors = preferredAnchors.concat(remainingAnchors);
  } else {
    anchors.sort((a, b) => {
      const adx = a.x - playerPos.x;
      const adz = a.z - playerPos.z;
      const bdx = b.x - playerPos.x;
      const bdz = b.z - playerPos.z;
      const ad = Math.sqrt(adx * adx + adz * adz);
      const bd = Math.sqrt(bdx * bdx + bdz * bdz);
      const aDelta = Math.min(Math.abs(ad - minSpawnDistance), Math.abs(ad - maxSpawnDistance));
      const bDelta = Math.min(Math.abs(bd - minSpawnDistance), Math.abs(bd - maxSpawnDistance));
      return aDelta - bDelta;
    });
  }

  let ai = 0;
  const pick = () => anchors[ai++ % Math.max(1, anchors.length)] || { x: 0, z: 0 };

  for (let i = 0; i < comp.drones; i++) {
    const a = pick();
    game.enemies.push(new Drone({ scene: game.scene, x: a.x, z: a.z, audio: game.audio }));
  }
  for (let i = 0; i < comp.enforcers; i++) {
    const a = pick();
    game.enemies.push(new Enforcer({ scene: game.scene, x: a.x, z: a.z, audio: game.audio }));
  }
  for (let i = 0; i < comp.zealots; i++) {
    const a = pick();
    game.enemies.push(new Zealot({ scene: game.scene, x: a.x, z: a.z, audio: game.audio }));
  }
  for (let i = 0; i < comp.minmons; i++) {
    const a = pick();
    game.enemies.push(new Minmon({ scene: game.scene, x: a.x, z: a.z, audio: game.audio }));
  }
  for (let i = 0; i < comp.ziluas; i++) {
    const a = pick();
    game.enemies.push(new Zilua({ scene: game.scene, x: a.x, z: a.z, audio: game.audio }));
  }
}




